import React, { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { routeGroups, RoutePaths } from "../../enumStrings";
import { UserType } from "../../graphql/types";

type PageContainerProps = {
  title?: string;
  description?: string;
  children: React.ReactNode;
};

const PageContainer: React.FC<PageContainerProps> = ({
  title: propTitle,
  description: propDescription,
  children,
}) => {
  const location = useLocation();

  const normalizePath = (path: string) => {
    const trimmed = path.replace(/\/+$/, "");
    return trimmed.length === 0 ? "/" : trimmed;
  };

  const currentPath = normalizePath(location.pathname);

  const shouldShowPersistentOverlay = (() => {
    const excludedPaths: string[] = [
      RoutePaths.error500,
      RoutePaths.login,
      RoutePaths.logout,
      RoutePaths.newPassword,
      RoutePaths.forgotPassword,
      RoutePaths.home,
      RoutePaths.chamberAdmin_support,
      RoutePaths.chamberAdmin_myChamber_manageChamber,
    ];
    const excludedSet = new Set(excludedPaths.map(normalizePath));
    return !excludedSet.has(currentPath);
  })();

  // Find the route info to get the title
  const findRouteInfo = () => {
    // Check all routes in routeGroups
    const allRoutes = routeGroups[UserType.ChamberAdmin];

    for (const item of allRoutes) {
      if (item.nestedRoute) {
        for (const route of item.nestedRoute.routes) {
          if (route.path === location.pathname) {
            return { title: route.title || "", path: route.path };
          }
        }
      } else if (item.route) {
        if (item.route.path === location.pathname) {
          return { title: item.route.title || "", path: item.route.path };
        }
      }
    }

    return { title: "", path: "", description: "" };
  };

  const routeInfo = findRouteInfo();
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the provided title or get it from the route
  const title = propTitle || routeInfo.title;
  const description = propDescription || routeInfo.description;

  const hasOverlay = shouldShowPersistentOverlay;

  useEffect(() => {
    if (!hasOverlay) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [hasOverlay]);

  const handlePreventScroll = useCallback(
    (e: React.UIEvent | React.WheelEvent | React.TouchEvent) => {
      e.preventDefault?.();
      e.stopPropagation?.();
      return false;
    },
    [],
  );

  // No longer need content rect since we center at 50% of viewport
  return (
    <div
      className="bg-white border-t border-l rounded-tl-[50px] min-h-screen flex flex-col shadow-sm relative"
      style={{ overflow: hasOverlay ? "hidden" : undefined }}
    >
      <div className="px-8 pt-8 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {description && (
          <p className="text-gray-900 text-lg mt-2">{description}</p>
        )}
      </div>
      <div ref={containerRef} className="flex-1 md:px-8 py-4 pt-2">
        {children}
      </div>

      {shouldShowPersistentOverlay && (
        <div
          className="absolute inset-0 bg-black bg-opacity-10 backdrop-blur-[1px]"
          style={{ zIndex: 5000 }}
          role="status"
          aria-live="polite"
          aria-busy={true}
          aria-label="Loading from local server"
          tabIndex={0}
          onWheel={handlePreventScroll as any}
          onTouchMove={handlePreventScroll as any}
        >
          <div
            className="text-center"
            style={{
              position: "fixed",
              left: `50%`,
              top: "50vh",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          >
            <div
              className="mx-auto w-[420px] max-w-[90vw]"
              style={{ pointerEvents: "auto" }}
            >
              <div className="bg-white bg-opacity-95 rounded-lg px-6 py-5 shadow-md border border-gray-200">
                <div className="text-gray-900 text-base font-semibold mb-3">
                  Loading from local server
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden relative">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: "78%",
                      backgroundColor: "#E1A731",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
                        animation: "shimmer 1.2s ease-in-out infinite",
                      }}
                    />
                  </div>
                  <style>{`
                    @keyframes shimmer {
                      0% { transform: translateX(-100%); }
                      100% { transform: translateX(100%); }
                    }
                  `}</style>
                </div>
                <div className="text-gray-500 text-xs mt-2 text-right">78%</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageContainer;
